import * as z from 'zod';

const eventAlertsBasePath = 'event-alerts/';

const sourceTypeZodEnum = z.enum(['scenario-insight', 'alfred']);
export type SourceType = z.infer<typeof sourceTypeZodEnum>;

const trackingStatusZodEnum = z.enum(['active', 'stopped', 'completed']);
export type TrackingStatus = z.infer<typeof trackingStatusZodEnum>;

export const postSubscriptionRequestSchema = z.object({
  source_id: z.string(),
  source_type: sourceTypeZodEnum,
  track: z.boolean(),
});
export type PostSubscriptionRequest = z.infer<
  typeof postSubscriptionRequestSchema
>;

const insightSubscriptionSchema = z.object({
  source_id: z.string(),
});

export const getSubscriptionResponseSchema = z.object({
  source_id: z.string(),
  source_type: sourceTypeZodEnum,
  tracking_status: trackingStatusZodEnum,
  subscriptions: z.array(insightSubscriptionSchema).optional(),
  next_cursor: z.string().optional(),
  prev_cursor: z.string().optional(),
});
export type GetSubscriptionResponse = z.infer<
  typeof getSubscriptionResponseSchema
>;

export const getSubscriptionsQuerySchema = z.object({
  source_type: sourceTypeZodEnum,
});
export type GetSubscriptionsQuery = z.infer<typeof getSubscriptionsQuerySchema>;

export const postReadRequestSchema = z.object({
  ids: z.array(z.string()),
});
export type PostReadRequest = z.infer<typeof postReadRequestSchema>;

const alertMetadataSchema = z.object({
  title: z.string().optional(),
  tracking_end_datetime: z.string().optional(),
  entity_tag: z.string().optional(),
  tracking_days_remaining: z.number().int().optional(),
  tracking_days_elapsed: z.number().int().optional(),
  payload: z
    .object({
      request_id: z.string(),
      status: z.enum(['success', 'error']),
      error: z.string().optional(),
    })
    .optional(),
});

const alertSchema = z.object({
  id: z.string(),
  source_id: z.string(),
  source_type: sourceTypeZodEnum,
  title: z.string().optional(),
  created_at: z.string(),
  read: z.boolean(),
  tracking_status: trackingStatusZodEnum.optional(),
  metadata: alertMetadataSchema.optional(),
});
export type Alert = z.infer<typeof alertSchema>;

export const postAlertsResponseSchema = z.object({
  alerts: z.array(alertSchema).nullish(),
  next_cursor: z.string().optional(),
  count: z.number().int().optional(),
});
export type PostAlertsResponse = z.infer<typeof postAlertsResponseSchema>;

export const postAlertsRequestSchema = z.object({
  type: sourceTypeZodEnum.optional(),
  read: z.boolean().optional(),
  limit: z.number().int().optional(),
  next: z.string().optional(),
  count_only: z.boolean().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});
export type PostAlertsRequest = z.infer<typeof postAlertsRequestSchema>;

export const eventAlerts = {
  postSubscriptions: {
    path: `${eventAlertsBasePath}subscriptions`,
  },
  getSubscriptions: {
    path: ({
      sourceType,
      sourceId,
      next,
    }: {
      sourceType: SourceType;
      sourceId?: string;
      next?: string;
    }) => {
      const params = new URLSearchParams(
        Object.entries({ source_type: sourceType, next }).filter(
          (entry): entry is [string, string] => entry[1] !== undefined
        )
      );
      return `${eventAlertsBasePath}subscriptions${
        sourceId ? `/${sourceId}` : ''
      }?${params.toString()}`;
    },
    schema: getSubscriptionResponseSchema,
  },
  postRead: {
    path: `${eventAlertsBasePath}read`,
  },
  postReadAll: {
    path: `${eventAlertsBasePath}read-all`,
  },
  postAlerts: {
    path: `${eventAlertsBasePath}alerts`,
    schema: postAlertsResponseSchema,
  },
};

export interface AlfredResponsePayload {
  title: string;
  body: string;
  metadata: {
    conversation_id: string;
    request_id: string;
    record_id?: string;
  };
  event: {
    type: 'conversation';
    action:
      | 'request.update'
      | 'request.success'
      | 'request.fail'
      | 'request.cancel';
  };
}

interface InsightAlertMetadata {
  title: string;
  tracking_end_datetime: string;
  entity_tag: string;
  tracking_days_remaining?: number;
  tracking_days_elapsed?: number;
}

interface AlfredAlertMetadata {
  payload: {
    request_id: string;
    status: 'success' | 'error';
    error?: string;
  };
}

export interface AlertsResponsePayload {
  id: string;
  source_id: string;
  metadata?: InsightAlertMetadata | AlfredAlertMetadata;
  event: {
    type: 'alert';
    action: 'alert.scenario-insight' | 'alert.alfred';
  };
}

export type NotificationSocketResponses =
  | AlfredResponsePayload
  | PortfolioNotificationPayload
  | AlertsResponsePayload;
export type NotificationResponseTypes =
  NotificationSocketResponses['event']['type'];

export type FindByType<Union, T> = Union extends { event: { type: T } }
  ? Union
  : never;

export type NotificationResponseByType<T extends NotificationResponseTypes> =
  FindByType<NotificationSocketResponses, T>;

export type PortfolioNotificationPayload = {
  title: string;
  body: string;
  event: {
    action: 'connection.success' | 'connection.error';
    type: 'portfolio';
  };
  metadata?: {
    portfolio_id: string;
    provider_id: string;
    provider_name: string;
  };
};
